 
import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Upload, CheckCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StatutoryChargesPage = () => {
  const [charges, setCharges] = useState([]);
  const [chargeTypes, setChargeTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [selectedType, setSelectedType] = useState('all');
  const [selectedMonth, setSelectedMonth] = useState('all');
  const [summary, setSummary] = useState(null);
  const [formData, setFormData] = useState({
    chargeType: '',
    termId: '',
    academicYear: '',
    monthNumber: '',
    expectedAmount: '',
    actualPaidAmount: '',
    paymentDate: '',
    paymentReference: ''
  });

  const fetchChargeTypes = useCallback(async () => {
    try {
      // Fetch statutory charge types
      const response = await fetch(`${API}/statutory-charges/settings/all`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      const data = await response.json();
      const fallbackTypes = [
        { charge_type: 'ZIMRA_PAYE', display_name: 'ZIMRA - PAYE (Income Tax)' },
        { charge_type: 'ZIMRA_AIDS_LEVY', display_name: 'ZIMRA - AIDS Levy' },
        { charge_type: 'NSSA_PENSION', display_name: 'NSSA - Pension Contribution' },
        { charge_type: 'NSSA_WORKERS_COMP', display_name: 'NSSA - Workers Compensation' },
        { charge_type: 'OTHER_STATUTORY', display_name: 'Other Statutory Charges' }
      ];
      setChargeTypes(Array.isArray(data) ? data : (Array.isArray(data?.settings) ? data.settings : fallbackTypes));
    } catch (error) {
      console.error('Error fetching charge types:', error);
      setChargeTypes([]);
    }
  }, []);

  const fetchSummary = useCallback(async () => {
    try {
      const termId = formData.termId || '1';
      const academicYear = formData.academicYear || '2024';
      const response = await fetch(`${API}/statutory-charges/summary/${termId}?academicYear=${academicYear}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      const data = await response.json();
      const items = Array.isArray(data?.items) ? data.items : [];
      const monthSummary = [1, 2, 3].reduce((acc, month) => {
        const monthItems = items.filter(item => Number(item.month_number) === month);
        acc[`month${month}`] = {
          zimra: monthItems.filter(item => String(item.charge_type || '').startsWith('ZIMRA')).reduce((sum, item) => sum + Number(item.actual || 0), 0),
          nssa: monthItems.filter(item => String(item.charge_type || '').startsWith('NSSA')).reduce((sum, item) => sum + Number(item.actual || 0), 0),
        };
        return acc;
      }, {});

      setSummary({
        zimra_total: items.filter(item => String(item.charge_type || '').startsWith('ZIMRA')).reduce((sum, item) => sum + Number(item.actual || 0), 0),
        nssa_total: items.filter(item => String(item.charge_type || '').startsWith('NSSA')).reduce((sum, item) => sum + Number(item.actual || 0), 0),
        ...monthSummary,
      });
    } catch (error) {
      console.error('Error fetching summary:', error);
      setSummary(null);
    }
  }, [formData.termId, formData.academicYear]);

  const fetchCharges = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (selectedType && selectedType !== 'all') params.append('chargeType', selectedType);
      if (selectedMonth && selectedMonth !== 'all') params.append('monthNumber', selectedMonth);

      const response = await fetch(`${API}/statutory-charges?${params}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      const data = await response.json();
      setCharges(Array.isArray(data) ? data : (Array.isArray(data?.charges) ? data.charges : []));
      fetchSummary();
    } catch (error) {
      console.error('Error fetching charges:', error);
      setCharges([]);
    } finally {
      setLoading(false);
    }
  }, [selectedType, selectedMonth, fetchSummary]);

  useEffect(() => {
    fetchChargeTypes();
  }, [fetchChargeTypes]);

  useEffect(() => {
    fetchCharges();
  }, [fetchCharges]);

  const handleAddCharge = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${API}/statutory-charges`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        setShowAddForm(false);
        setFormData({
          chargeType: '',
          termId: '',
          academicYear: '',
          monthNumber: '',
          expectedAmount: '',
          actualPaidAmount: '',
          paymentDate: '',
          paymentReference: ''
        });
        fetchSummary();
        fetchCharges();
      }
    } catch (error) {
      console.error('Error adding charge:', error);
    }
  };

  const handleMarkPaid = async (chargeId, actualAmount) => {
    try {
      const response = await fetch(`${API}/statutory-charges/${chargeId}/mark-paid`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          actualPaidAmount: actualAmount,
          paymentDate: new Date().toISOString().split('T')[0]
        })
      });

      if (response.ok) {
        fetchCharges();
      }
    } catch (error) {
      console.error('Error marking paid:', error);
    }
  };

  const handleUploadProof = async (chargeId) => {
    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.accept = '.pdf,.png,.jpg,.jpeg';
    fileInput.onchange = async (e) => {
      const file = e.target.files[0];
      if (file) {
        const formDataWithFile = new FormData();
        formDataWithFile.append('file', file);
        
        try {
          const response = await fetch(`${API}/statutory-charges/${chargeId}/upload-proof`, {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${localStorage.getItem('token')}`
            },
            body: formDataWithFile
          });

          if (response.ok) {
            fetchCharges();
          }
        } catch (error) {
          console.error('Error uploading proof:', error);
        }
      }
    };
    fileInput.click();
  };

  return (
    <div className="min-h-screen bg-gray-50 p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Statutory Charges</h1>
            <p className="text-gray-600 mt-2">Record ZIMRA and NSSA/NASSA obligations</p>
          </div>
          <Button onClick={() => setShowAddForm(!showAddForm)} className="gap-2">
            <Plus size={20} /> Record Charge
          </Button>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium">ZIMRA Total</CardTitle>
            </CardHeader>
            <CardContent>
                <div className="text-2xl font-bold text-blue-600">
                ${Number(summary?.zimra_total || 0).toFixed(2)}
              </div>
              <p className="text-xs text-gray-500 mt-1">Paid this term</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium">NSSA/NASSA Total</CardTitle>
            </CardHeader>
            <CardContent>
                <div className="text-2xl font-bold text-blue-600">
                ${Number(summary?.nssa_total || 0).toFixed(2)}
              </div>
              <p className="text-xs text-gray-500 mt-1">Paid this term</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium">Total Statutory</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-purple-600">
                ${(Number(summary?.zimra_total || 0) + Number(summary?.nssa_total || 0)).toFixed(2)}
              </div>
              <p className="text-xs text-gray-500 mt-1">All obligations</p>
            </CardContent>
          </Card>
        </div>

        {/* Monthly Breakdown */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Monthly Breakdown</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {[1, 2, 3].map(month => (
                <div key={month} className="border rounded-lg p-4">
                  <h4 className="font-semibold mb-2">Month {month}</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span>ZIMRA:</span>
                      <span className="font-semibold">${Number(summary?.[`month${month}`]?.zimra || 0).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>NSSA/NASSA:</span>
                      <span className="font-semibold">${Number(summary?.[`month${month}`]?.nssa || 0).toFixed(2)}</span>
                    </div>
                    <div className="border-t pt-2 flex justify-between font-bold">
                      <span>Total:</span>
                      <span>${(Number(summary?.[`month${month}`]?.zimra || 0) + Number(summary?.[`month${month}`]?.nssa || 0)).toFixed(2)}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Add Charge Form */}
        {showAddForm && (
          <Card className="mb-8">
            <CardHeader>
              <CardTitle>Record Statutory Charge</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleAddCharge} className="grid grid-cols-2 gap-4">
                <Select value={formData.chargeType} onValueChange={(value) => setFormData({...formData, chargeType: value})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Charge Type" />
                  </SelectTrigger>
                  <SelectContent>
                    {chargeTypes.map(type => (
                      <SelectItem key={type.charge_type} value={type.charge_type}>
                        {type.display_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select value={formData.monthNumber} onValueChange={(value) => setFormData({...formData, monthNumber: value})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Month" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">Month 1</SelectItem>
                    <SelectItem value="2">Month 2</SelectItem>
                    <SelectItem value="3">Month 3</SelectItem>
                  </SelectContent>
                </Select>

                <Input
                  type="number"
                  placeholder="Expected Amount"
                  value={formData.expectedAmount}
                  onChange={(e) => setFormData({...formData, expectedAmount: e.target.value})}
                />

                <Input
                  type="number"
                  placeholder="Actual Paid Amount"
                  value={formData.actualPaidAmount}
                  onChange={(e) => setFormData({...formData, actualPaidAmount: e.target.value})}
                />

                <Input
                  type="date"
                  placeholder="Payment Date"
                  value={formData.paymentDate}
                  onChange={(e) => setFormData({...formData, paymentDate: e.target.value})}
                />

                <Input
                  type="text"
                  placeholder="Payment Reference"
                  value={formData.paymentReference}
                  onChange={(e) => setFormData({...formData, paymentReference: e.target.value})}
                />

                <Button type="submit" className="col-span-2">Record Charge</Button>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Filters */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
          <Select value={selectedType} onValueChange={setSelectedType}>
            <SelectTrigger>
              <SelectValue placeholder="Filter by Type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              {chargeTypes.map(type => (
                <SelectItem key={type.charge_type} value={type.charge_type}>
                  {type.display_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={selectedMonth} onValueChange={setSelectedMonth}>
            <SelectTrigger>
              <SelectValue placeholder="Filter by Month" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Months</SelectItem>
              <SelectItem value="1">Month 1</SelectItem>
              <SelectItem value="2">Month 2</SelectItem>
              <SelectItem value="3">Month 3</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Charges Table */}
        <Card>
          <CardHeader>
            <CardTitle>Statutory Charges Records</CardTitle>
            <CardDescription>{charges.length} charges recorded</CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <p>Loading charges...</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="border-b">
                    <tr>
                      <th className="text-left py-2">Type</th>
                      <th className="text-left py-2">Month</th>
                      <th className="text-left py-2">Expected</th>
                      <th className="text-left py-2">Paid</th>
                      <th className="text-left py-2">Payment Date</th>
                      <th className="text-left py-2">Reference</th>
                      <th className="text-left py-2">Status</th>
                      <th className="text-left py-2">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {charges.map(charge => (
                      <tr key={charge.id} className="border-b hover:bg-gray-50">
                        <td className="py-3">{charge.display_name}</td>
                        <td className="py-3">{charge.month_number || '-'}</td>
                        <td className="py-3">${charge.expected_amount || '0.00'}</td>
                        <td className="py-3 font-semibold">${charge.actual_paid_amount || '0.00'}</td>
                        <td className="py-3">{charge.payment_date || '-'}</td>
                        <td className="py-3">{charge.payment_reference || '-'}</td>
                        <td className="py-3">
                          <span className={`px-2 py-1 rounded-full text-xs ${
                            charge.status === 'Paid' ? 'bg-green-100 text-green-800' :
                            'bg-yellow-100 text-yellow-800'
                          }`}>
                            {charge.status}
                          </span>
                        </td>
                        <td className="py-3 flex gap-2">
                          <button
                            onClick={() => handleUploadProof(charge.id)}
                            className="text-blue-600 hover:text-blue-800"
                            title="Upload Proof"
                          >
                            <Upload size={18} />
                          </button>
                          {charge.status === 'Pending' && (
                            <button
                              onClick={() => handleMarkPaid(charge.id, charge.actual_paid_amount)}
                              className="text-green-600 hover:text-green-800"
                              title="Mark as Paid"
                            >
                              <CheckCircle size={18} />
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default StatutoryChargesPage;
